/**
 * Template Utilities
 * Helper functions for template operations
 */

/**
 * Extract variable numbers from text (e.g., "Hello {{1}} and {{2}}" -> [1, 2])
 */
export const extractVariableNumbers = (text) => {
  const matches = (text || "").match(/\{\{(\d+)\}\}/g) || [];
  return matches
    .map((match) => parseInt(match.replace(/[{}]/g, "")))
    .filter((num) => !isNaN(num) && num > 0)
    .sort((a, b) => a - b);
};

/**
 * Renumber variables sequentially (1, 2, 3...)
 * Returns the updated text and a map of old -> new numbers
 */
export const renumberVariables = (text, maxVariables = 10) => {
  const variableNumbers = extractVariableNumbers(text);
  const uniqueNumbers = [...new Set(variableNumbers)].slice(0, maxVariables);

  // Create mapping from old numbers to new sequential numbers
  const renumberMap = {};
  uniqueNumbers.forEach((oldNum, index) => {
    const newNum = index + 1;
    renumberMap[oldNum] = newNum;
  });

  // Check if renumbering is needed
  const needsRenumbering = Object.keys(renumberMap).some(
    (oldNum) => renumberMap[oldNum] !== parseInt(oldNum)
  );

  if (!needsRenumbering) {
    return { text, renumberMap: {} };
  }

  // Replace variables in reverse order to avoid conflicts
  let updatedText = text;
  Object.keys(renumberMap)
    .sort((a, b) => parseInt(b) - parseInt(a))
    .forEach((oldNum) => {
      const newNum = renumberMap[oldNum];
      if (oldNum !== newNum.toString()) {
        const regex = new RegExp(`\\{\\{${oldNum}\\}\\}`, "g");
        updatedText = updatedText.replace(regex, `{{${newNum}}}`);
      }
    });

  return { text: updatedText, renumberMap };
};

/**
 * Get next variable number to insert
 */
export const getNextVariableNumber = (text, maxVariables = 10) => {
  const existingNumbers = extractVariableNumbers(text);
  if (existingNumbers.length === 0) return 1;
  const maxNumber = Math.max(...existingNumbers);
  return Math.min(maxNumber + 1, maxVariables);
};

/**
 * Build components array from flat template data
 */
export const buildComponentsFromData = (data) => {
  const components = [];

  // HEADER component
  if (data.headerFormat && data.headerFormat !== "NONE") {
    const headerComponent = {
      type: "HEADER",
      format: data.headerFormat,
    };
    if (data.headerText) {
      headerComponent.text = data.headerText;
    }
    components.push(headerComponent);
  }

  // BODY component
  if (data.body) {
    components.push({
      type: "BODY",
      text: data.body,
      example: null,
    });
  }

  // FOOTER component
  if (data.footer) {
    components.push({
      type: "FOOTER",
      text: data.footer,
    });
  }

  // BUTTONS component
  if (data.buttons && data.buttons.length > 0) {
    components.push({
      type: "BUTTONS",
      buttons: data.buttons,
    });
  }

  return components;
};

/**
 * Extract variables from template JSON with samples.
 * Handles both numbered ({{1}}) and named ({{customer_name}}) parameter formats.
 */
export const extractVariablesFromTemplateJson = (templateJson) => {
  const variables = [];
  
  if (typeof templateJson === 'string') {
    try {
      templateJson = JSON.parse(templateJson);
    } catch (e) {
      return variables;
    }
  }

  const jsonComponents = templateJson.components || [];

  const headerComponent = jsonComponents.find(c => c.type === 'HEADER');
  if (headerComponent && headerComponent.format === 'TEXT' && headerComponent.text) {
    const namedParams = headerComponent.example?.header_text_named_params;
    if (namedParams && Array.isArray(namedParams) && namedParams.length > 0) {
      namedParams.forEach((p, index) => {
        variables.push({
          id: Date.now() + index,
          name: p.param_name || `param_${index + 1}`,
          sample: p.example || '',
          target: 'header',
        });
      });
    } else {
      const headerVarMatches = headerComponent.text.match(/\{\{(\d+)\}\}/g) || [];
      headerVarMatches.forEach((match, index) => {
        const varNum = match.replace(/[{}]/g, '');
        const sample = headerComponent.example?.header_text?.[index] || '';
        variables.push({
          id: Date.now() + index,
          name: varNum,
          sample: sample,
          target: 'header',
        });
      });
    }
  }

  const bodyComponent = jsonComponents.find(c => c.type === 'BODY');
  if (bodyComponent && bodyComponent.text) {
    const namedParams = bodyComponent.example?.body_text_named_params;
    if (namedParams && Array.isArray(namedParams) && namedParams.length > 0) {
      namedParams.forEach((p, index) => {
        variables.push({
          id: Date.now() + 1000 + index,
          name: p.param_name || `param_${index + 1}`,
          sample: p.example || '',
          target: 'body',
        });
      });
    } else {
      const allVarMatches = bodyComponent.text.match(/\{\{[^}]+\}\}/g) || [];
      allVarMatches.forEach((match, index) => {
        const varName = match.replace(/[{}]/g, '');
        const sample = bodyComponent.example?.body_text?.[0]?.[index] || 
                      bodyComponent.example?.body_text?.[index] || '';
        variables.push({
          id: Date.now() + 1000 + index,
          name: varName,
          sample: sample,
          target: 'body',
        });
      });
    }
  }

  return variables;
};

/**
 * Get WABA account from localStorage
 * @deprecated Use PhoneAccountContext instead. This function is kept for backward compatibility only.
 */
export const getWabaAccount = () => {
  console.warn("getWabaAccount() is deprecated. Use PhoneAccountContext.selectedPhoneAccount instead.");
  try {
    const savedWaba = localStorage.getItem("selectedWabaAccount");
    return savedWaba ? JSON.parse(savedWaba) : null;
  } catch (err) {
    console.error("Error parsing saved WABA account:", err);
    return null;
  }
};

/**
 * Build API payload from template data
 */
export const buildTemplatePayload = (templateData, wabaAccount) => {
  const parameterFormat = templateData.parameter_format || "numbered";

  const components = templateData.components.map((component) => {
    const componentCopy = { ...component };

    // Add examples for BODY component
    if (component.type === "BODY") {
      const bodyVars = templateData.variables
        .filter((v) => v.target === "body")
        .sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
      if (bodyVars.length > 0) {
        if (parameterFormat === "named") {
          componentCopy.example = {
            body_text_named_params: bodyVars.map((v) => ({
              param_name: v.param_name || `param_${v.name}`,
              example: v.sample || "",
            })),
          };
        } else {
          componentCopy.example = {
            body_text: [bodyVars.map((v) => v.sample)],
          };
        }
      }
    }

    // Add examples for HEADER component (TEXT format)
    if (component.type === "HEADER" && component.format === "TEXT") {
      const headerVars = templateData.variables
        .filter((v) => v.target === "header")
        .sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10))
        .filter((v) => v.sample && v.sample.trim() !== "");
      if (headerVars.length > 0) {
        if (parameterFormat === "named") {
          componentCopy.example = {
            header_text_named_params: headerVars.map((v) => ({
              param_name: v.param_name || `param_${v.name}`,
              example: v.sample || "",
            })),
          };
        } else {
          componentCopy.example = {
            header_text: headerVars.map((v) => v.sample),
          };
        }
      }
    }

    // Handle media headers
    if (
      component.type === "HEADER" &&
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(component.format)
    ) {
      if (templateData.header_media_url) {
        componentCopy.example = {
          header_handle: [templateData.header_media_url],
        };
      } else {
        componentCopy.example = {
          header_handle: ["MEDIA_ID_PLACEHOLDER"],
        };
      }
    }

    // Clean up button objects
    if (component.type === "BUTTONS" && component.buttons) {
      componentCopy.buttons = component.buttons.map((btn) => {
          const buttonObj = {
            type: btn.type,
            text: btn.text,
          };

          if (btn.type === "URL") {
            if (btn.url) buttonObj.url = btn.url;
            if (btn.url_type) buttonObj.url_type = btn.url_type;
            if (btn.urlVariable && btn.urlVariable.sample) {
              buttonObj.example = [btn.urlVariable.sample];
            } else if (
              btn.example &&
              Array.isArray(btn.example) &&
              btn.example.length > 0
            ) {
              buttonObj.example = btn.example;
            }
          } else if (btn.type === "PHONE_NUMBER") {
            const countryCode = btn.country_code || "+91";
            const phone = (btn.phone_number || "").replace(/\D/g, "");
            buttonObj.phone_number = countryCode && phone ? `${countryCode}${phone}` : btn.phone_number || "";
          } else if (btn.type === "WHATSAPP_CALL") {
            if (btn.active_for_days) buttonObj.active_for_days = btn.active_for_days;
          } else if (btn.type === "COPY_OFFER_CODE") {
            if (btn.offer_code) buttonObj.offer_code = btn.offer_code;
          } else if (btn.type === "FLOW") {
            if (btn.flow_id) buttonObj.flow_id = btn.flow_id;
          }

          return buttonObj;
        });
    }

    return componentCopy;
  });

  return {
    name: templateData.name,
    language: templateData.language,
    category: templateData.category.toUpperCase(),
    components,
    waba_id: wabaAccount?.wabaId || templateData.wabaId,
    phone_id: wabaAccount?.phoneId || templateData.phoneId,
  };
};

